import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Link,
} from '@mui/material';

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(num);
};

const formatNumber = (num, decimals = 4) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatEpochOrIso = (value) => {
  if (value === null || value === undefined) return 'N/A';
  try {
    // Trade events use epoch seconds; the wallet indexes use ISO strings.
    const ms = typeof value === 'number' ? value * 1000 : Date.parse(value);
    if (!Number.isFinite(ms)) return String(value);
    return new Date(ms).toLocaleString();
  } catch {
    return String(value);
  }
};

const truncateHash = (hash) => {
  if (!hash) return '-';
  return hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-6)}` : hash;
};

const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all', textAlign: 'right' }}>{value}</Typography>
  </Box>
);

const HyperliquidItemCard = ({ hit }) => {
  const ev = hit._source || {};
  const direction = (ev.direction || '').toUpperCase();
  const token = ev.token_symbol || ev.token_address || '-';
  const explorerUrl = ev.wallet_address ? `https://hyperdash.info/trader/${ev.wallet_address}` : null;

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card elevation={1} sx={{ width: '100%', backgroundColor: '#fafafa', overflow: 'visible' }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
                {direction || 'TRADE'} · {token}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                {ev.wallet_address || '-'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                {direction && (
                  <Chip
                    size="small"
                    label={direction}
                    color={direction === 'BUY' ? 'success' : direction === 'SELL' ? 'error' : 'default'}
                  />
                )}
                {ev.chain && <Chip size="small" label={ev.chain} variant="outlined" />}
                {ev.token_symbol && <Chip size="small" label={ev.token_symbol} variant="outlined" />}
              </Box>
            </Box>
            <Chip label={hit._score != null ? hit._score.toFixed(2) : '-'} size="small" variant="outlined" color="primary" />
          </Box>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Trade</Typography>
            <MetricRow label="Size" value={formatCurrency(ev.size_usd)} />
            <MetricRow label="Price" value={formatCurrency(ev.price_usd)} />
            <MetricRow label="Bought" value={ev.token_bought_amount ? `${formatNumber(ev.token_bought_amount)} ${ev.token_bought_symbol || ''}`.trim() : '-'} />
            <MetricRow label="Sold" value={ev.token_sold_amount ? `${formatNumber(ev.token_sold_amount)} ${ev.token_sold_symbol || ''}`.trim() : '-'} />
          </Paper>

          {(ev.holders_count > 0 || ev.volume_usd_24h != null || ev.price_diff_pct_1d != null) && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Market</Typography>
              <MetricRow label="Holders" value={formatNumber(ev.holders_count, 0)} />
              <MetricRow label="Volume 24h" value={formatCurrency(ev.volume_usd_24h)} />
              <MetricRow label="Price Δ 1d" value={ev.price_diff_pct_1d != null ? `${formatNumber(ev.price_diff_pct_1d, 4)}%` : '-'} />
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Identifiers</Typography>
            <MetricRow label="Tx Hash" value={truncateHash(ev.transaction_hash)} />
            <MetricRow label="Token" value={ev.token_address || '-'} />
          </Paper>

          <Paper elevation={0} sx={{ p: 1.5, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Timestamps</Typography>
            <MetricRow label="Trade Time" value={formatEpochOrIso(ev.timestamp)} />
            <MetricRow label="Indexed At" value={formatEpochOrIso(ev.indexed_at)} />
          </Paper>

          {explorerUrl && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: '0.7rem' }}>
                <Link href={explorerUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  {ev.wallet_address}
                </Link>
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default HyperliquidItemCard;
